import { searchableComputedProperty, SB } from "@play-co/replicant";
import { chunk } from "../arrayUtils.mjs";
import { getEntryTimestamps } from "./getEntryTimestamps.mjs";
const DAY_IN_MS = 1e3 * 60 * 60 * 24;
function createAsyncGettersForAnalytics(opts) {
  return {
    getEntryTimestamps: async (playerIds, api) => {
      const states = await api.fetchStates(playerIds);
      return getEntryTimestamps(playerIds, (id) => {
        var _a, _b;
        const state = (_a = states[id]) == null ? void 0 : _a.state;
        if (!state) {
          return void 0;
        }
        if (opts == null ? void 0 : opts.getPlatformStorage) {
          return opts.getPlatformStorage(state);
        }
        const castState = state;
        return (_b = castState.gcinstant) == null ? void 0 : _b.platformStorage;
      });
    },
    getFriendAnalytics: async (args, api) => {
      if (!isAnalyticsComputedProperties(opts.computedProperties)) {
        throw Error(
          "Missing required searchable computed properties for the getFriendAnalytics async getter: `friendIds` and `updatedAt`"
        );
      }
      const friendIds = [...args.friendIds];
      const now = api.date.now();
      const threeMonthsAgo = new Date(now).setMinutes(0, 0, 0) - DAY_IN_MS * 90;
      const fields = ["friendIds", "updatedAt"];
      const hasAgeGroup = !!opts.computedProperties.ageGroup;
      if (hasAgeGroup) {
        fields.push("ageGroup");
      }
      const immediateFriendsSearch = await api.searchPlayers({
        fields,
        where: {
          id: { isOneOf: friendIds },
          updatedAt: { greaterThan: threeMonthsAgo }
        },
        limit: Math.min(friendIds.length, 1e4)
        // TODO handle >10,000 friends
      });
      const ageGroupDistribution = {
        adult: 0,
        teen: 0,
        unknown: 0
      };
      const immediateFriends = {};
      for (const friend of immediateFriendsSearch.results) {
        immediateFriends[friend.id] = {
          friendIds: friend.friendIds || [],
          updatedAt: friend.updatedAt
        };
        const ageGroup = friend.ageGroup === "adult" ? "adult" : friend.ageGroup === "teen" ? "teen" : "unknown";
        ageGroupDistribution[ageGroup] += 1;
      }
      const activeFriendCounts = {
        ...calculateActiveFriendCounts(immediateFriends, now),
        playerFriendCount: friendIds.length
      };
      immediateFriends[api.getUserID()] = { friendIds, updatedAt: now };
      const unfetchedFriendsOfFriends = getUnfetchedFriendsOfFriendIds(immediateFriends);
      const clusterFriends = { ...immediateFriends };
      while (unfetchedFriendsOfFriends.length > 0) {
        const batchIds = unfetchedFriendsOfFriends.splice(0, 1e4);
        const friendsOfFriendsSearch = await api.searchPlayers({
          fields: ["friendIds", "updatedAt"],
          where: {
            id: { isOneOf: batchIds },
            updatedAt: { greaterThan: threeMonthsAgo }
          },
          limit: batchIds.length
        });
        for (const friendOfFriend of friendsOfFriendsSearch.results) {
          clusterFriends[friendOfFriend.id] = {
            friendIds: friendOfFriend.friendIds || [],
            updatedAt: friendOfFriend.updatedAt
          };
        }
      }
      const clusterCounts = calculateClusterCounts(clusterFriends, now);
      return { activeFriendCounts, ageGroupDistribution, clusterCounts };
    },
    lineGetDebugFriendCounts: async (opts2, api) => {
      const socialFriendIdsBatches = chunk(opts2.socialFriendIds, 1e3);
      let playingFriendCount = 0;
      for (const socialFriendIdsBatch of socialFriendIdsBatches) {
        playingFriendCount += await api.countPlayers({
          where: { id: { isOneOf: socialFriendIdsBatch } }
        });
      }
      return { playingFriendCount };
    },
    lineIsSubscribedToChatbot: (_, api) => api.chatbot.isSubscribed()
  };
}
function getUnfetchedFriendsOfFriendIds(immediateFriends) {
  const friendsOfFriendsSet = /* @__PURE__ */ new Set();
  for (const immediateFriend of Object.values(immediateFriends)) {
    for (const friendId of immediateFriend.friendIds) {
      if (!immediateFriends[friendId]) {
        friendsOfFriendsSet.add(friendId);
        if (friendsOfFriendsSet.size === 1e5) {
          return Array.from(friendsOfFriendsSet);
        }
      }
    }
  }
  return Array.from(friendsOfFriendsSet);
}
({
  ageGroup: searchableComputedProperty(SB.string().optional(), () => void 0),
  friendIds: searchableComputedProperty(SB.array(SB.string()), () => []),
  updatedAt: searchableComputedProperty(SB.int(), () => 0)
});
function isAnalyticsComputedProperties(computedProperties, api) {
  var _a, _b;
  const { ageGroup, friendIds, updatedAt } = computedProperties;
  const ageGroupIsValid = !ageGroup || ageGroup._searchable && (ageGroup.type instanceof SB.StringSchema || ageGroup.type instanceof SB.TupleSchema);
  return ageGroupIsValid && (friendIds == null ? void 0 : friendIds._searchable) && (friendIds == null ? void 0 : friendIds.type) instanceof SB.ArraySchema && ((_b = friendIds == null ? void 0 : (_a = friendIds.type).getItemSchema) == null ? void 0 : _b.call(_a)) instanceof SB.StringSchema && (updatedAt == null ? void 0 : updatedAt._searchable) && (updatedAt == null ? void 0 : updatedAt.type) instanceof SB.IntegerSchema;
}
function calculateActiveFriendCounts(friends, now) {
  function activeFriendCount(days) {
    return Object.values(friends).filter(
      (friend) => now - friend.updatedAt < DAY_IN_MS * days
    ).length;
  }
  return {
    activeFriendCount1D: activeFriendCount(1),
    activeFriendCount3D: activeFriendCount(3),
    activeFriendCount7D: activeFriendCount(7),
    activeFriendCount14D: activeFriendCount(14),
    activeFriendCount30D: activeFriendCount(30),
    activeFriendCount90D: activeFriendCount(90)
  };
}
function calculateClusterCounts(clusterFriends, now) {
  const res = {
    clusterSize1D: 0,
    clusterSize3D: 0,
    clusterSize7D: 0,
    clusterSize14D: 0,
    clusterSize30D: 0,
    clusterSize90D: 0,
    clusterConnections1D: 0,
    clusterConnections3D: 0,
    clusterConnections7D: 0,
    clusterConnections14D: 0,
    clusterConnections30D: 0,
    clusterConnections90D: 0,
    clusterConnectedness1D: 0,
    clusterConnectedness3D: 0,
    clusterConnectedness7D: 0,
    clusterConnectedness14D: 0,
    clusterConnectedness30D: 0,
    clusterConnectedness90D: 0
  };
  for (const { friendIds, updatedAt } of Object.values(clusterFriends)) {
    for (const days of [1, 3, 7, 14, 30, 90]) {
      if (now - updatedAt < DAY_IN_MS * days) {
        res[`clusterSize${days}D`] += 1;
      }
    }
    for (const friendId of friendIds) {
      if (!clusterFriends[friendId]) {
        continue;
      }
      for (const days of [1, 3, 7, 14, 30, 90]) {
        if (now - updatedAt < DAY_IN_MS * days && now - clusterFriends[friendId].updatedAt < DAY_IN_MS * days) {
          res[`clusterConnections${days}D`] += 1;
        }
      }
    }
  }
  res.clusterConnectedness1D = res.clusterSize1D ? res.clusterConnections1D / res.clusterSize1D : 0;
  res.clusterConnectedness3D = res.clusterSize3D ? res.clusterConnections3D / res.clusterSize3D : 0;
  res.clusterConnectedness7D = res.clusterSize7D ? res.clusterConnections7D / res.clusterSize7D : 0;
  res.clusterConnectedness14D = res.clusterSize14D ? res.clusterConnections14D / res.clusterSize14D : 0;
  res.clusterConnectedness30D = res.clusterSize30D ? res.clusterConnections30D / res.clusterSize30D : 0;
  res.clusterConnectedness90D = res.clusterSize90D ? res.clusterConnections90D / res.clusterSize90D : 0;
  return res;
}
export {
  calculateClusterCounts,
  createAsyncGettersForAnalytics
};
